import { MailListItemSingleLineViewProps } from './MailListItemComponentProps';
import onCategoryClicked from '../utils/onCategoryClicked';
import renderReactions from '../utils/renderReactions';
import classnamesBind from 'classnames/bind';
import { observer } from 'mobx-react-lite';
import { CategoryWell } from 'owa-categories';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getListViewColumnWidths } from 'owa-mail-layout';
import { onMailListItemClickHandler } from 'owa-mail-list-item-shared';
import { TableView } from 'owa-mail-list-store';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import styles from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';
import { getMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/getMessageListTextStyle';
import { mailStore } from 'owa-mail-store';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import shouldShowUnstackedReadingPane from 'owa-mail-store/lib/utils/shouldShowUnstackedReadingPane';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import * as React from 'react';
import {
    MailListItemExternalMarker,
    MailListItemFirstLineText,
    MailListItemIconBar,
    MailListItemLineText,
    MailListItemPreviewDisplay,
    MailListItemTwistyButton,
    MailListItemUnreadCount,
    PersonaCheckbox,
    MailListItemMeetingInfo,
    MailListItemTxpInfo,
    FolderTag,
    MailListItemRichPreviewWell,
    MailListItemTimestamp,
} from 'owa-mail-list-item-components';

const classNames = classnamesBind.bind(styles);

export default observer(function MailListItemSingleLineView(
    props: MailListItemSingleLineViewProps
): JSX.Element {
    const {
        backgroundColorClass,
        canDelete,
        canPin,
        categories,
        conversationId,
        densityModeString,
        effectiveMentioned,
        firstLineText,
        firstLineTooltipText,
        hasAttachment,
        hasSharepointLink,
        highlightTerms,
        importance,
        isComplete,
        isFirstLevelExpanded,
        isFlagged,
        isInArchiveFolder,
        isInNotesFolder,
        isLoadingSecondLevelExpansion,
        isNudged,
        isPinned,
        isSecondLevelExpanded,
        isSelected,
        isSnoozed,
        isTaggedForBigScreen,
        isUnauthenticatedSender,
        itemClassIcon,
        lastDeliveryTimestamp,
        lastSender,
        latestItemId,
        likeCount,
        listViewType,
        mailboxGuids,
        parentFolderId,
        returnTime,
        rowId,
        rowKey,
        secondLineText,
        secondLineTooltipText,
        shouldShowAsSpotlight,
        shouldShowExternalLabel,
        shouldShowTwisty,
        showAttachmentPreview,
        showCondensedView,
        showDraftsIndicator,
        showFolderTag,
        showPreviewText,
        smimeType,
        styleSelectorAsPerUserSettings,
        supportsFlagging,
        supportsHoverIcons,
        supportsPinning,
        tableViewId,
        thirdLineText,
        unreadCount,
        shouldShowRSVP,
        shouldShowTxpButton,
    } = props;

    // derived properties based on props/flights
    const hasDensityNext = isFeatureEnabled('mon-densities');
    const usingColumnHeaders = isFeatureEnabled('mon-tri-columnHeadersSlv');
    const hasBigHoverAction = isFeatureEnabled('mon-tri-mailListItemHoverDeleteSlv');
    const isUnread = unreadCount > 0;
    const shouldShowCategories = categories?.length > 0 && !isNudged;
    const showAttachmentPreviews = !showCondensedView && showAttachmentPreview;
    const showPreviews =
        !shouldShowUnstackedReadingPane() ||
        (!isFirstLevelExpanded && !isSecondLevelExpanded && !isLoadingSecondLevelExpansion);

    const tableView: TableView = (listViewStore.tableViews.get(tableViewId) as TableView)!;
    const isItemView = tableView.tableQuery.listViewType === ReactListViewType.Message;

    let firstColumnStyles;
    if (isItemView) {
        firstColumnStyles = usingColumnHeaders ? styles.firstColumnWithHeaders : styles.firstColumn;
    } else {
        const stylesWithTwisty = usingColumnHeaders
            ? styles.firstColumnWithHeaders
            : styles.firstColumn;
        const stylesWithoutTwisty = usingColumnHeaders
            ? styles.firstColumnNoTwistyWithHeaders
            : styles.firstColumnNoTwisty;

        firstColumnStyles = shouldShowTwisty ? stylesWithTwisty : stylesWithoutTwisty;
    }

    const secondColumnStyle = usingColumnHeaders
        ? styles.secondColumnWithHeaders
        : styles.secondColumn;

    const subjectClasses = classNames(
        isUnread ? styles.subjectUnread : styles.subject,
        isNudged && styles.shouldShrink,
        shouldShowCategories && styles.paddingLeftForSubjectInSingleLine,
        hasDensityNext && getMessageListTextStyle('Major', densityModeString)
    );

    const { senderColumnWidth, subjectColumnWidth } = getListViewColumnWidths(tableView);

    const firstColumnWidth = usingColumnHeaders
        ? {
              width: senderColumnWidth,
              maxWidth: senderColumnWidth,
          }
        : {};

    const secondColumnWidth = usingColumnHeaders
        ? {
              width: subjectColumnWidth,
              maxWidth: subjectColumnWidth,
              paddingLeft: '16px',
          }
        : {};

    const receivedColumnWidth = getListViewColumnWidths(tableView).receivedColumnWidth;
    const thirdColumnWidth = usingColumnHeaders
        ? {
              width: receivedColumnWidth,
              maxWidth: receivedColumnWidth,
          }
        : {};

    const isLoadingExpansion = useComputedValue((): boolean => {
        const expansionState = listViewStore.expandedConversationViewState;
        const conversationItemParts = mailStore.conversations.get(conversationId);
        return !!(
            (isFirstLevelExpanded || isSecondLevelExpanded) &&
            (expansionState.forks == [] ||
                (conversationItemParts && conversationItemParts.loadingState.isLoading))
        );
    }, [isFirstLevelExpanded, isSecondLevelExpanded, conversationId]);

    const onPreviewClick = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            onMailListItemClickHandler(
                evt,
                MailListItemSelectionSource.MailListItemRichPreview,
                rowKey,
                tableViewId
            );
        },
        [rowKey, tableViewId]
    );

    const folderTagComponent = (
        <FolderTag
            folderId={parentFolderId}
            isPreviewTextOn={showPreviewText || isNudged}
            isSingleLine={true}
            mailboxGuids={mailboxGuids}
        />
    );

    const timestampComponent = returnTime ? (
        <MailListItemTimestamp isSnoozed={true} returnTime={returnTime ?? ''} />
    ) : (
        <MailListItemTimestamp
            isSingleLine={true}
            isUnread={isUnread}
            lastDeliveryTimestamp={lastDeliveryTimestamp}
            returnTime={returnTime ?? ''}
        />
    );

    const renderIconBarComponent = (
        neverShowHoverIcons: boolean,
        neverShowPropertyIcons: boolean
    ) => {
        return (
            <MailListItemIconBar
                canDelete={canDelete}
                canPin={canPin}
                effectiveMentioned={effectiveMentioned}
                hasAttachment={hasAttachment}
                hasSharepointLink={hasSharepointLink}
                importance={importance}
                isComplete={isComplete}
                isFlagged={isFlagged}
                isInArchiveFolder={isInArchiveFolder}
                isInNotesFolder={isInNotesFolder}
                isPinned={isPinned}
                isRowExpanded={isFirstLevelExpanded || isSecondLevelExpanded}
                isSingleLine={true}
                isSnoozed={isSnoozed}
                isTaggedForBigScreen={isTaggedForBigScreen}
                itemClassIcon={itemClassIcon}
                lastDeliveryTimestamp={lastDeliveryTimestamp}
                likeCount={likeCount}
                neverShowHoverIcons={neverShowHoverIcons || !supportsHoverIcons}
                neverShowPropertyIcons={neverShowPropertyIcons}
                rowKey={rowKey}
                shouldShowAsSpotlight={shouldShowAsSpotlight}
                smimeType={smimeType}
                supportsFlagging={supportsFlagging}
                supportsPinning={supportsPinning}
                tableViewId={tableViewId}
                unreadCount={unreadCount}
            />
        );
    };

    return (
        <div className={styles.singleLineViewContainer}>
            <div
                className={classNames(
                    densityModeString,
                    usingColumnHeaders ? styles.firstRowBase : styles.firstRow,
                    hasDensityNext && styles.slvDensityNext
                )}>
                <div
                    className={classNames(
                        firstColumnStyles,
                        usingColumnHeaders && densityModeString
                    )}
                    style={firstColumnWidth}>
                    {shouldShowTwisty && (
                        <MailListItemTwistyButton
                            isLoadingExpansion={isLoadingExpansion}
                            isSecondLevelExpanded={isSecondLevelExpanded}
                            isSelected={isSelected}
                            isSingleLine={true}
                            isUnread={unreadCount > 0}
                            rowKey={rowKey}
                            shouldShowTwisty={shouldShowTwisty}
                            showCondensedView={showCondensedView}
                            tableViewId={tableViewId}
                        />
                    )}
                    <PersonaCheckbox
                        tableViewId={tableViewId}
                        isSelected={isSelected}
                        lastSender={lastSender}
                        rowKey={rowKey}
                        lastDeliveryTimestamp={lastDeliveryTimestamp}
                        isUnauthenticatedSender={isUnauthenticatedSender}
                        styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                    />
                    {shouldShowExternalLabel && (
                        <MailListItemExternalMarker isSelected={isSelected} />
                    )}
                    <MailListItemFirstLineText
                        highlightTerms={highlightTerms}
                        showDraftsIndicator={showDraftsIndicator}
                        unreadCount={unreadCount}
                        text={firstLineText}
                        tooltipText={firstLineTooltipText ?? ''}
                    />
                    {renderIconBarComponent(
                        isFeatureEnabled(
                            'mon-tri-mailListItemHoverDeleteSlv'
                        ) /* neverShowHoverIcons */,
                        false /* neverShowPropertyIcons */
                    )}
                </div>
                <div className={secondColumnStyle} style={secondColumnWidth}>
                    <div className={styles.content}>
                        {shouldShowCategories && (
                            <CategoryWell
                                key={rowId}
                                categoryWellContainerClass={styles.categoryWellSingleLine}
                                categoryContainerClass={styles.categoryContainerSingleLine}
                                categories={categories}
                                onCategoryClicked={onCategoryClicked}
                            />
                        )}

                        <div className={subjectClasses}>
                            <MailListItemLineText
                                addOverflowStyles={false}
                                highlightTerms={highlightTerms}
                                text={secondLineText}
                                tooltipText={secondLineTooltipText ?? ''}
                            />
                            {!isFeatureEnabled('mon-tri-unreadCountBadgeWithBackground') && (
                                <MailListItemUnreadCount unreadCount={unreadCount} />
                            )}
                        </div>
                        {(showPreviewText || isNudged) && (
                            <div
                                className={classNames(
                                    styles.previewDisplaySingleLine,
                                    isNudged && styles.isNudged
                                )}>
                                {showPreviews && (
                                    <MailListItemPreviewDisplay
                                        highlightTerms={highlightTerms}
                                        isNudged={isNudged}
                                        previewDisplay={thirdLineText}
                                        rowKey={rowKey}
                                        tableViewId={tableViewId}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    {isFeatureEnabled('tri-message-list-reactions') &&
                        renderReactions({
                            backgroundColorClass: backgroundColorClass,
                            isFirstLevelExpanded: isFirstLevelExpanded,
                            isSecondLevelExpanded: isSecondLevelExpanded,
                            latestItemId: latestItemId,
                            reactionsContainerClassName: styles.reactionsContainer,
                            rowId: rowId,
                            tableViewId: tableViewId,
                        })}
                    {shouldShowRSVP && (
                        <MailListItemMeetingInfo
                            conversationId={conversationId}
                            isSelected={isSelected}
                            isSingleLineView={true}
                            itemClassIcon={itemClassIcon}
                            latestItemId={latestItemId}
                            listViewType={listViewType}
                            tableViewId={tableViewId}
                        />
                    )}
                    {shouldShowTxpButton && (
                        <MailListItemTxpInfo
                            latestItemId={latestItemId}
                            tableViewId={tableViewId}
                            isSingleLineView={true}
                        />
                    )}
                    {showFolderTag && usingColumnHeaders && folderTagComponent}
                </div>
                {/* SLV THIRD COLUMN STARTS HERE */}
                <div
                    className={hasBigHoverAction ? styles.slvBigHoverThirdColumn : undefined}
                    style={thirdColumnWidth}>
                    <div
                        className={
                            hasBigHoverAction ? styles.slvBigHoverThirdColumnTextWrapper : undefined
                        }>
                        {showFolderTag && !usingColumnHeaders ? (
                            <div
                                className={classNames(
                                    usingColumnHeaders
                                        ? styles.thirdColumnWithFolderTagWithHeaders
                                        : styles.thirdColumnWithFolderTag,
                                    hasDensityNext &&
                                        getMessageListTextStyle('Minor', densityModeString)
                                )}>
                                {folderTagComponent}
                                {timestampComponent}
                            </div>
                        ) : (
                            timestampComponent
                        )}
                    </div>
                    {hasBigHoverAction && (
                        <div className={styles.thirdColumnIconBarWithHeaders}>
                            {renderIconBarComponent(
                                false /* neverShowHoverIcons */,
                                true /* neverShowPropertyIcons */
                            )}
                        </div>
                    )}
                </div>
                {/* SLV THIRD COLUMN ENDS HERE */}
            </div>
            {showPreviews && showAttachmentPreviews && (
                <MailListItemRichPreviewWell
                    isWideView={true}
                    onPreviewClick={onPreviewClick}
                    rowId={rowId}
                    tableViewId={tableViewId}
                />
            )}
        </div>
    );
});
